import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { articles, categories as allCategories } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = allCategories.find((c) => c.slug === slug) || allCategories[0];

  const categoryArticles = useMemo(
    () =>
      articles
        .filter((a) => a.categorySlug === (slug || 'lianai-jiqiao'))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [slug]
  );

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${currentCategory.name} - 爱情岛论坛亚洲`,
            url: `https://www.aiqingdao.com/category/${slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiqingdao.com/' },
                { '@type': 'ListItem', position: 2, name: '分类浏览', item: `https://www.aiqingdao.com/category/lianai-jiqiao` },
                { '@type': 'ListItem', position: 3, name: currentCategory.name },
              ],
            },
          }),
        }}
      />

      <div className="py-8 md:py-10 px-4 md:px-6">
        <div className="w-full max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览' },
                { label: currentCategory.name },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${currentCategory.icon} text-lg text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {allCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  cat.slug === (slug || 'lianai-jiqiao')
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </button>
            ))}
          </div>

          {/* Articles List */}
          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col rounded-xl bg-background-100 hover:bg-background-200 transition-colors overflow-hidden cursor-pointer"
                >
                  <div className="h-[200px] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - 爱情岛论坛亚洲`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h2 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2 flex-1">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full overflow-hidden bg-background-200">
                          <img
                            src={article.authorAvatar}
                            alt={article.author}
                            className="w-full h-full object-cover"
                          />
                        </span>
                        {article.author}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i> {article.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}